// A single interaction model for mouse, touch and pen. All listeners are passive:
// native vertical scrolling and pinch zoom always take priority over the artwork.
export function bindParticlePointer(element, { project, isReduced, tapDuration = 220 }) {
  const state = { x: -9999, y: -9999, active: false, contact: false, input: 'mouse', pointerId: null, sequence: 0, lastEnd: '' };
  let startedAt = 0, releaseAt = 0, suppressMouseUntil = 0;
  const listeners = [];
  const listen = (node, name, handler) => { node.addEventListener(name, handler, { passive: true }); listeners.push([node, name, handler]); };
  const locate = event => {
    const point = project(event);
    if (!point || !Number.isFinite(point.x) || !Number.isFinite(point.y)) return false;
    state.x = point.x; state.y = point.y;
    return true;
  };
  const reset = (reason = '') => {
    state.active = false; state.contact = false; state.pointerId = null;
    state.lastEnd = reason; releaseAt = 0;
  };
  const down = event => {
    if (isReduced()) { reset('reduced-motion'); return; }
    const input = event.pointerType || 'mouse';
    if (input === 'mouse') {
      if (event.button !== 0 || performance.now() < suppressMouseUntil) return;
      if (locate(event)) { state.input = 'mouse'; state.active = true; state.sequence++; }
      return;
    }
    if (event.isPrimary === false || state.pointerId !== null) return;
    if (!locate(event)) return;
    state.input = input; state.contact = true; state.active = true;
    state.pointerId = event.pointerId; state.sequence++; state.lastEnd = '';
    startedAt = performance.now(); releaseAt = startedAt + tapDuration;
    suppressMouseUntil = startedAt + 800;
  };
  const move = event => {
    if (isReduced()) { reset('reduced-motion'); return; }
    const input = event.pointerType || 'mouse';
    if (input !== 'mouse') {
      if (state.contact && state.pointerId === event.pointerId && locate(event)) state.active = true;
      return;
    }
    if (state.contact || performance.now() < suppressMouseUntil) return;
    if (locate(event)) {
      if (!state.active || state.input !== 'mouse') state.sequence++;
      state.input = 'mouse'; state.active = true;
    }
  };
  const up = event => {
    if (state.pointerId !== event.pointerId) return;
    const now = performance.now();
    state.contact = false; state.pointerId = null; state.lastEnd = 'up';
    // A quick tap gets a small, finite pulse even if it falls between two frames.
    // A held touch starts returning as soon as the finger lifts.
    releaseAt = Math.max(now, startedAt + tapDuration);
    state.active = now < releaseAt; suppressMouseUntil = now + 700;
  };
  const cancel = event => {
    if (state.pointerId === event.pointerId) { suppressMouseUntil = performance.now() + 700; reset('cancel'); }
  };
  const leave = () => { if (state.input === 'mouse') reset('leave'); };
  const blur = () => reset('blur');
  const visibility = () => { if (document.hidden) reset('hidden'); };
  listen(element, 'pointerdown', down);
  listen(element, 'pointermove', move);
  listen(element, 'pointerleave', leave);
  listen(window, 'pointerup', up);
  listen(window, 'pointercancel', cancel);
  listen(window, 'blur', blur);
  listen(document, 'visibilitychange', visibility);
  return {
    read(now = performance.now()) {
      if (isReduced()) reset('reduced-motion');
      else if (!state.contact && state.input !== 'mouse' && now >= releaseAt) state.active = false;
      return state;
    },
    reset,
    dispose() { reset('disposed'); listeners.forEach(([node, name, handler]) => node.removeEventListener(name, handler)); },
  };
}
